import ApiHandler from '../ApiHandler';
import { urls } from '../urls';

export interface AlaCarteProduct {
  id: number;
  product_id: string;
  name: string;
  description: string;
  features: string[];
  price: string;
  case_credits: number;
  demand_letter_credits: number;
  deposition_credits: number;
  stripe_price_id: string;
  is_active: boolean;
  use_case_groups: Array<{
    id: number;
    name: string;
    order: number;
  }>;
}

export interface UserAlaCarteCredit {
  case_credits: number;
  demand_letter_credits: number;
  deposition_credits: number;
  total_credits: number;
  updated_at: string | null;
}

export interface AlaCartePurchase {
  id: number;
  product: AlaCarteProduct;
  quantity: number;
  total_price: string;
  stripe_payment_intent_id: string;
  purchase_date: string;
}

export interface AlaCarteCreditUsage {
  id: number;
  credit_type: string;
  amount: number;
  case: any; // Case object
  related_object_id: number | null;
  used_at: string;
}

/**
 * API service for à la carte product management
 */
export class AlaCarteApi {
  /**
   * Get all available à la carte products
   */
  static async getProducts(): Promise<AlaCarteProduct[]> {
    const data = await ApiHandler.get<AlaCarteProduct[]>(
      urls.alaCarte.products
    );
    return data as any;
  }

  /**
   * Get user's current à la carte credits
   */
  static async getCredits(): Promise<UserAlaCarteCredit> {
    const data = await ApiHandler.get<UserAlaCarteCredit>(
      urls.alaCarte.credits
    );
    return data as any;
  }

  /**
   * Create Payment Intent for à la carte purchase (in-app payment)
   */
  static async createPaymentIntent(
    items: any[]
  ): Promise<{ client_secret: string; payment_intent_id: string }> {
    const data = await ApiHandler.post<{
      client_secret: string;
      payment_intent_id: string;
    }>(urls.alaCarte.createPaymentIntent, { items });
    return data as any;
  }

  /**
   * Purchase à la carte products
   */
  static async purchase(
    items: any[],
    stripePaymentIntentId: string
  ): Promise<any> {
    const data = await ApiHandler.post<any>(urls.alaCarte.purchase, {
      items,
      stripe_payment_intent_id: stripePaymentIntentId,
    });
    return data as any;
  }

  /**
   * Get user's purchase history
   */
  static async getPurchases(): Promise<AlaCartePurchase[]> {
    const data = await ApiHandler.get<AlaCartePurchase[]>(
      urls.alaCarte.purchases
    );
    return data as any;
  }

  /**
   * The receipt Stripe issued for a one-time purchase (ZLAYT-1476). Rejects
   * when Stripe has none.
   */
  static async getPurchaseReceiptUrl(purchaseId: number): Promise<string> {
    const data: any = await ApiHandler.get<{ receipt_url: string }>(
      urls.alaCarte.receipt(purchaseId)
    );
    if (!data?.receipt_url) throw new Error('No receipt for this purchase');
    return data.receipt_url;
  }

  /**
   * Get user's credit usage history
   */
  static async getUsage(): Promise<AlaCarteCreditUsage[]> {
    const data = await ApiHandler.get<AlaCarteCreditUsage[]>(
      urls.alaCarte.usage
    );
    return data as any;
  }

  /**
   * Purchase à la carte products directly using saved payment method
   * @param items Array of product items to purchase
   * @param paymentMethodId Optional specific payment method ID to use (uses default if not provided)
   */
  static async purchaseDirect(
    items: any[],
    paymentMethodId?: string
  ): Promise<any> {
    const payload: any = { items };
    if (paymentMethodId) {
      payload.payment_method_id = paymentMethodId;
    }
    const data = await ApiHandler.post<any>(
      urls.alaCarte.purchaseDirect,
      payload
    );
    return data as any;
  }
}
